import React from "react";

import AlertCircleIcon from "@uui/AlertCircle";
import PlusIcon from "@uui/Plus";
import { isEmpty } from "lodash/fp";

import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import type { TopicImpersonationView } from "@app/analytics/types";
import AccessSelectorItem from "@app/common/access/ShareReport/AccessSelector/AccessSelectorItem";
import {
  AccessSelectorDropdown,
  EntityList,
} from "@app/common/access/ShareReport/styles";
import LINKS from "@app/common/links";
import { getInitials } from "@app/common/utils";

import Avatar from "@rui/Avatar";
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
} from "@rui/EmptyStateContainer";
import Link from "@rui/Link";
import LoadSpinner from "@rui/LoadSpinner";
import Stack, { Row } from "@rui/Stack";

const EntityWithAccess = ({
  entity,
  onRemove,
}: {
  entity: TopicImpersonationView;
  onRemove: (entityId: string) => void;
}) => {
  const entityDisplayValue =
    entity.kind === "email" ? entity.entityId : entity.name;

  const handleRemove = () => onRemove(entity.entityId);

  return (
    <Row variant="apart" spacing={1}>
      <Row variant="left" spacing={1} sx={{ overflow: "hidden" }}>
        <Avatar name={entityDisplayValue}>
          {getInitials(entityDisplayValue)}
        </Avatar>
        <Typography
          title={entityDisplayValue}
          variant="bodyPrimaryReg"
          color="shades.neutral.600"
          noWrap
        >
          {entityDisplayValue}
        </Typography>
      </Row>
      <AccessSelectorDropdown
        name="access"
        wrapperSx={{ height: "100%", flexShrink: 0 }}
        selectedItem={{ value: "viewer", label: "Viewer" }}
      >
        <AccessSelectorItem
          selected
          value="viewer"
          label="Viewer"
          description="Can view notes section of report and all future updates"
        />
        <Divider sx={{ mx: 2 }} />
        <AccessSelectorItem
          key="remove"
          value="remove"
          label="Remove Access"
          onClick={handleRemove}
        />
      </AccessSelectorDropdown>
    </Row>
  );
};

const AccessList = ({
  entities,
  isError = false,
  isLoading = false,
  onRemove,
  maxHeight,
}: {
  entities: TopicImpersonationView[];
  isError?: boolean;
  isLoading?: boolean;
  onRemove: (entityId: string) => void;
  maxHeight?: React.CSSProperties["maxHeight"];
}) => {
  return (
    <EntityList spacing={1} maxHeight={maxHeight}>
      {isLoading ? (
        <Stack variant="center" flex={1}>
          <LoadSpinner />
        </Stack>
      ) : isEmpty(entities) ? (
        <EmptyStateContainer>
          <EmptyStateIcon
            Icon={isError ? AlertCircleIcon : PlusIcon}
            padding={2}
          />
          {isError ? (
            <EmptyStateSubtext>
              Something went wrong. Please refresh the page or contact
              <Link href={LINKS.EMAIL.SUPPORT}>support@read.ai</Link> if the
              problem persists.
            </EmptyStateSubtext>
          ) : (
            <EmptyStateSubtext>
              You're not sharing with anyone
            </EmptyStateSubtext>
          )}
        </EmptyStateContainer>
      ) : (
        entities.map((entity) => (
          <EntityWithAccess
            key={entity.entityId}
            entity={entity}
            onRemove={onRemove}
          />
        ))
      )}
    </EntityList>
  );
};

export default AccessList;
